import { useEffect, useState, useRef } from "react";
import { useParams } from "react-router-dom";
import {
  doc,
  getDoc,
  collection,
  query,
  where,
  getDocs,
  addDoc,
  serverTimestamp,
} from "firebase/firestore";
import { auth } from "../../firebase/firebase";
import { db } from "../../firebase/firebase";

function PublicProfile() {
  /* =========================
     ROUTE PARAM (TOKEN)
  ========================= */
  const { token } = useParams();
  const viewerEmail = auth.currentUser?.email || null;

  /* =========================
     RESOLVED FOUNDER
  ========================= */
  const [founderId, setFounderId] = useState(null);
  const [linkError, setLinkError] = useState("");

  const [profile, setProfile] = useState(null);
  const [pitch, setPitch] = useState(null);
  const [claims, setClaims] = useState([]);
  const [groupedClaims, setGroupedClaims] = useState({});
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [evidences, setEvidences] = useState({});
  const [startupRecords, setStartupRecords] = useState([]);

  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  /* =========================
     REQUEST ACCESS STATE
  ========================= */
  const [requestEmail, setRequestEmail] = useState("");
  const [requestMessage, setRequestMessage] = useState("");
  const [requestSent, setRequestSent] = useState(false);
  const [requestError, setRequestError] = useState("");

  /* =========================
     ANALYTICS DEDUPE GUARDS
  ========================= */
  const viewedClaims = useRef(new Set());
  const openedPitch = useRef(false);
  const openedEvidence = useRef(new Set());

  /* =========================
     Section refs
  ========================= */
  const profileRef = useRef(null);
  const pitchRef = useRef(null);
  const recordsRef = useRef(null);
  const claimsRef = useRef(null);

  const scrollTo = (ref) => {
    ref?.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  /* =========================
     Analytics logger
  ========================= */
  const logEvent = (event) => {
    try {
      addDoc(collection(db, "analytics"), {
        ownerId: founderId,
        source: "public",
        ...event,
        timestamp: serverTimestamp(),
        userAgent: navigator.userAgent,
      });
    } catch {
      // silent
    }
  };

  /* =========================
     VALIDATE SHARE TOKEN
  ========================= */
  useEffect(() => {
    const validateToken = async () => {
      try {
        const tokenSnap = await getDoc(
          doc(db, "publicProfileShares", token)
        );

        if (!tokenSnap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const data = tokenSnap.data();

        if (data.revoked === true) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        if (data.expiresAt?.toDate() < new Date()) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        setFounderId(data.founderId);
      } catch {
        setNotFound(true);
        setLoading(false);
      }
    };

    if (token) validateToken();
  }, [token]);

  /* =========================
     LOAD PROFILE + DATA
  ========================= */
  useEffect(() => {
    const loadData = async () => {
      if (!founderId) return;

      try {
        const userRef = doc(db, "users", founderId);
        const pitchRef = doc(db, "pitches", founderId);

        const claimsQ = query(
          collection(db, "claims"),
          where("userId", "==", founderId)
        );

        const evQ = query(
          collection(db, "evidences"),
          where("userId", "==", founderId)
        );

        const recordsQ = query(
          collection(db, "startupRecords"),
          where("userId", "==", founderId)
        );

        const [
          userSnap,
          pitchSnap,
          claimsSnap,
          evSnap,
          recordsSnap,
        ] = await Promise.all([
          getDoc(userRef),
          getDoc(pitchRef),
          getDocs(claimsQ),
          getDocs(evQ),
          getDocs(recordsQ),
        ]);

        if (!userSnap.exists()) {
          setNotFound(true);
          setLoading(false);
          return;
        }

        const userData = userSnap.data();
        setProfile(userData);

        if (pitchSnap.exists()) {
          setPitch(pitchSnap.data());
        }

        const claimsData = claimsSnap.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        }));
        setClaims(claimsData);

        const grouped = {};
        claimsData.forEach((c) => {
          const group = c.group || "Other";
          if (!grouped[group]) grouped[group] = [];
          grouped[group].push(c);
        });
        setGroupedClaims(grouped);

        const collapsed = {};
        Object.keys(grouped).forEach((g) => {
          collapsed[g] = false;
        });
        setCollapsedGroups(collapsed);

        const evGrouped = {};
        evSnap.docs.forEach((d) => {
          const data = d.data();
          if (!evGrouped[data.claimId]) evGrouped[data.claimId] = [];
          evGrouped[data.claimId].push({ id: d.id, ...data });
        });
        setEvidences(evGrouped);

        setStartupRecords(
          recordsSnap.docs.map((d) => ({
            id: d.id,
            ...d.data(),
          }))
        );

        setLoading(false);
      } catch {
        setNotFound(true);
        setLoading(false);
      }
    };

    loadData();
  }, [founderId, viewerEmail]);

  /* =========================
     CLAIM VIEW LOGGING
  ========================= */
  useEffect(() => {
    claims.forEach((claim) => {
      if (!viewedClaims.current.has(claim.id)) {
        viewedClaims.current.add(claim.id);
        logEvent({
          eventType: "claim_view",
          entityType: "claim",
          entityId: claim.id,
        });
      }
    });
  }, [claims]);

  /* =========================
     RENDER GUARDS
  ========================= */
  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          fontSize: 16,
        }}
      >
        Loading startup profile…
      </div>
    );
  }

  if (notFound) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#64748b",
          textAlign: "center",
        }}
      >
        <div>
          <h2>Profile not found</h2>
          <p>This link may be invalid or expired.</p>
        </div>
      </div>
    );
  }

  if (!profile) return null;

  const approvedViewers = profile.approvedViewers || [];
  const isPrivate = profile.profileVisibility === "private";
  const isApproved =
    !isPrivate || (viewerEmail && approvedViewers.includes(viewerEmail));

  if (!isApproved) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            maxWidth: 420,
            padding: 32,
            background: "#fff",
            borderRadius: 16,
          }}
        >
          <h2>🔒 This profile is private</h2>
          <p>This startup profile is not publicly accessible.</p>
        </div>
      </div>
    );
  }

  const getRiskBadgeStyle = (risk) => {
    switch (risk?.toLowerCase()) {
      case "high":
        return { background: "#fee2e2", color: "#991b1b" };
      case "medium":
        return { background: "#fef3c7", color: "#92400e" };
      case "low":
        return { background: "#dcfce7", color: "#166534" };
      default:
        return { background: "#e5e7eb", color: "#334155" };
    }
  };

  return (
    <div style={{ display: "flex", minHeight: "100vh" }}>
      {/* =========================
          SIDE MENU
      ========================= */}
      <aside
        style={{
          width: 220,
          backgroundColor: "#020617",
          color: "#c7d2fe",
          padding: "32px 20px",
          position: "sticky",
          top: 0,
          height: "100vh",
        }}
      >
        <div
          style={{
            fontSize: 18,
            fontWeight: 600,
            color: "#fff",
            marginBottom: 32,
          }}
        >
          Prufyy
        </div>

        <div
          style={{ cursor: "pointer", marginBottom: 14 }}
          title="Overview of the startup, team, and core details"
          onClick={() => scrollTo(profileRef)}
        >
          Profile
        </div>

        <div
          style={{ cursor: "pointer", marginBottom: 14 }}
          title="View the founder’s pitch deck"
          onClick={() => scrollTo(pitchRef)}
        >
          Pitch
        </div>

        {startupRecords.length > 0 && (
          <div
            style={{ cursor: "pointer", marginBottom: 14 }}
            title="Official documents and verification records"
            onClick={() => scrollTo(recordsRef)}
          >
            Startup Records
          </div>
        )}

        <div
          style={{ cursor: "pointer" }}
          title="All claims made by the founder and their supporting evidence"
          onClick={() => scrollTo(claimsRef)}
        >
          Claims
        </div>
      </aside>
      {/* =========================
          MAIN CONTENT
      ========================= */}
      <main style={{ flex: 1, padding: "48px 32px", maxWidth: 1100 }}>
        {/* =========================
            PROFILE
        ========================= */}
        <section ref={profileRef}>
          <div
            style={{
              padding: 32,
              borderRadius: 16,
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              marginBottom: 48,
              display: "grid",
              gridTemplateColumns: "1fr 300px",
              gap: 32,
              alignItems: "center",
            }}
          >
            <div>
              <h1 style={{ fontSize: 34, fontWeight: 700 }}>
                {profile.startupName}

                {profile.verification?.status === "verified" && (
                  <span
                    title="This startup has been verified by Prufyy"
                    style={{
                      marginLeft: 10,
                      fontSize: 14,
                      color: "#2563eb",
                      cursor: "help",
                    }}
                  >
                    ✔ Verified by Prufyy
                  </span>
                )}
              </h1>

              {Array.isArray(profile.industries) &&
                profile.industries.length > 0 && (
                  <div
                    style={{
                      marginTop: 10,
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    {profile.industries.map((ind) => (
                      <span
                        key={ind}
                        title="Industry category selected by the founder"
                        style={{
                          padding: "4px 10px",
                          borderRadius: 999,
                          background: "#eef2ff",
                          fontSize: 13,
                          color: "#3730a3",
                          cursor: "help",
                        }}
                      >
                        {ind}
                      </span>
                    ))}
                  </div>
                )}

              <div
                style={{
                  marginTop: 10,
                  fontSize: 14,
                  color: "#475569",
                }}
                title="Founder name and role in the startup"
              >
                Founded by{" "}
                <strong>
                  {profile.fullName}
                  {profile.founderRole && (
                    <span style={{ fontWeight: 400 }}>
                      {" "}
                      ({profile.founderRole})
                    </span>
                  )}
                </strong>
              </div>

              {profile.email && (
                <div
                  style={{ marginTop: 4, fontSize: 13 }}
                  title="Click to email the founder"
                >
                  <a href={`mailto:${profile.email}`}>
                    {profile.email}
                  </a>
                </div>
              )}

              <p
                style={{ marginTop: 16, fontSize: 16 }}
                title="Founder-provided description of the startup"
              >
                {profile.description}
              </p>

              {profile.website && (
                <a
                  href={profile.website}
                  target="_blank"
                  rel="noreferrer"
                  title="Visit the startup’s official website"
                >
                  {profile.website}
                </a>
              )}
            </div>

            {profile.startupLogoUrl && (
              <img
                src={profile.startupLogoUrl}
                alt="Startup logo"
                title="Startup logo uploaded by the founder"
                style={{
                  maxHeight: 180,
                  maxWidth: 300,
                  objectFit: "contain",
                }}
              />
            )}
          </div>
        </section>
        {/* =========================
            PITCH
        ========================= */}
        {pitch && (
          <section ref={pitchRef}>
            <div
              style={{
                padding: 24,
                background: "#fff",
                borderRadius: 12,
                border: "1px solid #e5e7eb",
                marginBottom: 48,
              }}
            >
              <h2
                title="Founder’s uploaded pitch deck or primary presentation"
                style={{ cursor: "help" }}
              >
                Pitch Deck
              </h2>

              <a
                href={pitch.currentFileUrl}
                target="_blank"
                rel="noreferrer"
                title="Open or download the pitch deck (this action is logged)"
                onClick={() => {
                  if (!openedPitch.current) {
                    openedPitch.current = true;
                    logEvent({
                      eventType: "pitch_open",
                      entityType: "pitch",
                    });
                  }
                }}
              >
                📥 View / Download current pitch
              </a>
            </div>
          </section>
        )}

        {/* =========================
            STARTUP RECORDS
        ========================= */}
        {startupRecords.length > 0 && (
          <section ref={recordsRef} style={{ marginBottom: 48 }}>
            <h2
              title="Official startup documents shared separately from pitch claims"
              style={{ cursor: "help" }}
            >
              Startup Records
            </h2>

            <p
              style={{ fontSize: 14, color: "#64748b", marginBottom: 12 }}
              title="These documents support the startup’s legal or operational status"
            >
              Verified documents and reference links shared by the founder
            </p>

            {startupRecords.map((rec) => (
              <div key={rec.id} style={{ marginBottom: 10 }}>
                <a
                  href={`/record/${rec.id}?token=${token}`}
                  target="_blank"
                  rel="noreferrer"
                  title={
                    rec.type === "link"
                      ? "External document or reference link"
                      : "Uploaded official document"
                  }
                >
                  {rec.type === "link" ? "🔗" : "📄"} {rec.title}
                </a>
              </div>
            ))}
          </section>
        )}
        {/* =========================
            CLAIMS & EVIDENCE
        ========================= */}
        <section ref={claimsRef}>
          <h2
            title="Each claim is evaluated independently with its own supporting evidence"
            style={{ cursor: "help" }}
          >
            Claims & Evidence
          </h2>

          <p
            style={{
              fontSize: 14,
              color: "#64748b",
              marginBottom: 20,
              maxWidth: 720,
            }}
            title="Claims are intentionally separated so each statement can be verified on its own"
          >
            The startup’s key statements are broken into individual claims.
            Each claim may have one or more pieces of supporting evidence.
          </p>

          {Object.entries(groupedClaims).map(
            ([group, groupClaims]) => (
              <div key={group} style={{ marginBottom: 36 }}>
                {/* GROUP HEADER */}
                <div
                  style={{
                    cursor: "pointer",
                    fontWeight: 600,
                    marginBottom: 12,
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}
                  title="Click to expand or collapse this claim category"
                  onClick={() =>
                    setCollapsedGroups((p) => ({
                      ...p,
                      [group]: !p[group],
                    }))
                  }
                >
                  <span>{collapsedGroups[group] ? "▶" : "▼"}</span>
                  <span>
                    {group} ({groupClaims.length})
                  </span>
                </div>

                {/* CLAIMS */}
                {!collapsedGroups[group] &&
                  groupClaims.map((claim) => (
                    <div
                      key={claim.id}
                      style={{
                        padding: 20,
                        background: "#fff",
                        borderRadius: 12,
                        border: "1px solid #e5e7eb",
                        marginBottom: 14,
                      }}
                    >
                      {/* CLAIM TEXT */}
                      <div
                        style={{
                          fontWeight: 600,
                          marginBottom: 10,
                        }}
                        title="This is a single, reviewable claim made by the founder"
                      >
                        {claim.text}
                      </div>

                      {/* RISK BADGE */}
                      {claim.riskLevel && (
                        <span
                          title={
                            claim.riskLevel.toLowerCase() === "high"
                              ? "High-risk claims require strong, verifiable evidence"
                              : claim.riskLevel.toLowerCase() === "medium"
                              ? "Moderate-risk claims benefit from supporting proof"
                              : "Low-risk claims are typically easier to verify"
                          }
                          style={{
                            ...getRiskBadgeStyle(claim.riskLevel),
                            padding: "4px 10px",
                            borderRadius: 999,
                            fontSize: 12,
                            display: "inline-block",
                            marginBottom: 10,
                            cursor: "help",
                          }}
                        >
                          {claim.riskLevel.toUpperCase()} RISK
                        </span>
                      )}

                      {/* EVIDENCE LIST */}
                      {(evidences[claim.id] || []).length > 0 ? (
                        <div style={{ marginTop: 10 }}>
                          <div
                            style={{
                              fontSize: 13,
                              color: "#64748b",
                              marginBottom: 6,
                            }}
                            title="Each item below is directly linked to this specific claim"
                          >
                            Supporting evidence:
                          </div>

                          {(evidences[claim.id] || []).map(
                            (ev) => (
                              <div key={ev.id} style={{ marginBottom: 6 }}>
                                <a
                                  href={`/evidence/${ev.id}?token=${token}`}
                                  target="_blank"
                                  rel="noreferrer"
                                  title={
                                    ev.type === "link"
                                      ? "External reference or live source"
                                      : "Uploaded document provided by the founder"
                                  }
                                  onClick={() => {
                                    if (
                                      !openedEvidence.current.has(
                                        ev.id
                                      )
                                    ) {
                                      openedEvidence.current.add(
                                        ev.id
                                      );
                                      logEvent({
                                        eventType:
                                          "evidence_open",
                                        entityType:
                                          "evidence",
                                        entityId: ev.id,
                                      });
                                    }
                                  }}
                                >
                                  {ev.type === "link"
                                    ? "🔗"
                                    : "📄"}{" "}
                                  {ev.label}
                                </a>
                              </div>
                            )
                          )}
                        </div>
                      ) : (
                        <div
                          style={{
                            marginTop: 8,
                            fontSize: 13,
                            color: "#94a3b8",
                          }}
                          title="This claim has no attached evidence yet"
                        >
                          No evidence attached to this claim.
                        </div>
                      )}
                    </div>
                  ))}
              </div>
            )
          )}
        </section>
      </main>
    </div>
  );
}

export default PublicProfile;
